// @intent: Pure traversal helpers for ProjectBodyV1 scene_graph Layer trees
// @flow: render-model compiler and runtime highlights call these helpers before any renderer adapter sees data
// @stale: update when Layer geometry or scene_graph nesting semantics change

import type { Layer } from '../types';

export interface LayerBoundsInRoot {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function childrenOf(layer: Layer): readonly Layer[] {
  switch (layer.type) {
    case 'scene':
    case 'group':
      return layer.children;
    default:
      return [];
  }
}

function layerBounds(layer: Layer): { readonly width: number; readonly height: number } | null {
  switch (layer.type) {
    case 'rect':
    case 'sprite':
    case 'button':
      return { width: layer.width, height: layer.height };
    default:
      return null;
  }
}

export function collectAssetUrls(root: Layer | undefined): string[] {
  if (!root) return [];

  const urls: string[] = [];
  function visit(node: Layer): void {
    if (node.type === 'rect' && node.assetUrl) {
      urls.push(node.assetUrl);
    }
    if (node.type === 'sprite') {
      urls.push(node.assetUrl);
    }
    if (node.type === 'button' && node.assetUrl) {
      urls.push(node.assetUrl);
    }

    for (const child of childrenOf(node)) {
      visit(child);
    }
  }

  visit(root);
  return urls;
}

export function findLayer(root: Layer, id: string): Layer | null {
  if (root.id === id) return root;

  for (const child of childrenOf(root)) {
    const found = findLayer(child, id);
    if (found) return found;
  }

  return null;
}

export function countLayers(root: Layer | undefined): number {
  if (!root) return 0;
  return 1 + childrenOf(root).reduce((sum, child) => sum + countLayers(child), 0);
}

export function resolveLayerBoundsInRoot(root: Layer, id: string): LayerBoundsInRoot | null {
  function visit(node: Layer, offsetX: number, offsetY: number): LayerBoundsInRoot | null {
    const x = offsetX + node.x;
    const y = offsetY + node.y;
    const bounds = layerBounds(node);
    if (node.id === id && bounds) {
      return { id: node.id, x, y, width: bounds.width, height: bounds.height };
    }

    for (const child of childrenOf(node)) {
      const found = visit(child, x, y);
      if (found) return found;
    }

    return null;
  }

  return visit(root, 0, 0);
}
